import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { reverseGeocode } from "./api.ts";

/**
 * Delivery-location picker modelled on Telegram's native location screen: the
 * pin stays pinned to the centre of the viewport and the *map* pans underneath
 * it. Wherever the crosshair lands is the drop-off — every settle reverse-
 * geocodes (via the backend) to prefill the address. Precise coordinates travel
 * with the order so staff can navigate.
 *
 * The pin is a plain DOM overlay (not a Leaflet marker), so it never scrolls
 * with the tiles and sidesteps Leaflet's classic broken marker-image problem.
 */

// Groningen city centre — the shop's service area default.
const DEFAULT_CENTER: [number, number] = [53.2194, 6.5665];
// CartoDB "Voyager": a clean, low-contrast basemap close to Telegram's own map.
const TILE_URL =
  "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";

export function MapPicker({
  onCoords,
  onAddress,
}: {
  onCoords: (lat: number, lng: number) => void;
  onAddress: (address: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const geocodeTimer = useRef<number | null>(null);
  const [resolving, setResolving] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Commit the map's current centre as the chosen location: order coords now,
  // and (debounced) the reverse-geocoded address.
  const commitCenter = () => {
    const map = mapRef.current;
    if (!map) return;
    const { lat, lng } = map.getCenter();
    onCoords(lat, lng);

    if (geocodeTimer.current !== null) clearTimeout(geocodeTimer.current);
    setResolving(true);
    geocodeTimer.current = window.setTimeout(async () => {
      const place = await reverseGeocode(lat, lng).catch(() => null);
      setResolving(false);
      if (place) onAddress(place.address);
    }, 400);
  };

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: 15,
      attributionControl: false,
      zoomControl: false,
    });
    L.tileLayer(TILE_URL, { maxZoom: 20, detectRetina: true }).addTo(map);
    L.control.zoom({ position: "bottomright" }).addTo(map);

    // Lift the pin while panning (Telegram-style), settle + geocode on stop.
    map.on("movestart", () => setDragging(true));
    map.on("moveend", () => {
      setDragging(false);
      commitCenter();
    });

    mapRef.current = map;
    // Seed the initial address from the default centre so the field isn't blank.
    commitCenter();

    return () => {
      if (geocodeTimer.current !== null) clearTimeout(geocodeTimer.current);
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) return;
    setResolving(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        mapRef.current?.setView(
          [pos.coords.latitude, pos.coords.longitude],
          16,
        );
        // moveend from setView triggers commitCenter; clear the spinner in case
        // the view didn't actually change.
        setResolving(false);
      },
      () => setResolving(false),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="map-wrap">
      <div className="map-stage">
        <div ref={containerRef} className="map-canvas" />
        {/* Fixed centre pin — the crosshair the map pans under. */}
        <div className={`map-centerpin ${dragging ? "is-lifted" : ""}`}>
          <div className="map-centerpin-head" />
          <div className="map-centerpin-shadow" />
        </div>
        <button
          type="button"
          className="map-locate"
          onClick={useMyLocation}
          aria-label="Use my location"
        >
          {resolving ? "◎" : "⌖"}
        </button>
      </div>
      <p className="map-hint">Drag the map to place the pin on your delivery spot.</p>
    </div>
  );
}
